import { css } from '@emotion/react';
import { Button, Typography } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { colors } from '../../../../theme/colors';
import { LayoutCanvas } from './components/LayoutCanvas';
import { PanelPalette } from './components/PanelPalette';
import { DraggablePanelData } from './components/PanelCard';
import { DraggableCanvasItemData } from './components/PanelSlot';
import { useLayoutActions } from './hooks/useLayoutActions';
import { TearsheetPanelType } from './types';
import { GRID_COLS, GRID_MAX_ROWS, GRID_ROW_HEIGHT, GRID_WIDTH, TEARSHEET_PANELS } from './constants';
import { GridRect, findValidDropRect, createOccupancyGrid } from './utils/gridOccupancy';

const CELL_WIDTH = GRID_WIDTH / GRID_COLS;
const CELL_HEIGHT = GRID_ROW_HEIGHT;

const containerStyle = css`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 180px);
`;

const headerStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0 1rem 0;
  border-bottom: 1px solid ${colors.neutral[20]};
`;

const headerLeftStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const mainContentStyle = css`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const dragOverlayStyle = css`
  background: ${colors.neutral[5]};
  border: 1px solid ${colors.neutral[30]};
  border-radius: 4px;
  padding: 0.5rem;
  opacity: 0.9;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

type DragSource = 'palette' | 'canvas';

interface ActiveDragState {
  source: DragSource;
  panelType: TearsheetPanelType;
  itemId?: string; // Only for canvas items
  w: number;
  h: number;
  // Offset from mouse position to item's top-left corner (in grid cells)
  offsetX: number;
  offsetY: number;
}

export function TearsheetV2LayoutBuilder() {
  const { layout, usedPanels, addPanel, removePanel, resizePanel, moveAndResizePanel, resetLayout } =
    useLayoutActions();
  const canvasRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef(1);

  // Drag state
  const [activeDrag, setActiveDrag] = useState<ActiveDragState | null>(null);
  const [previewRect, setPreviewRect] = useState<GridRect | null>(null);
  const [cursorGridPos, setCursorGridPos] = useState<{ x: number; y: number } | null>(null);
  const [isOverCanvas, setIsOverCanvas] = useState(false);

  const handleScaleChange = useCallback((scale: number) => {
    scaleRef.current = scale;
  }, []);

  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement required to start drag
      },
    })
  );

  // Convert pointer position to grid coordinates (accounting for canvas scale)
  // Returns coordinates even if outside bounds - findValidDropRect handles clamping
  const pointerToGrid = useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) {
      return null;
    }

    const scale = scaleRef.current;
    // Convert screen coordinates to unscaled canvas coordinates
    const relativeX = (clientX - rect.left) / scale;
    const relativeY = (clientY - rect.top) / scale;

    return {
      x: Math.floor(relativeX / CELL_WIDTH),
      y: Math.floor(relativeY / CELL_HEIGHT),
    };
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as DraggablePanelData | DraggableCanvasItemData | undefined;

    if (data?.source === 'palette') {
      const paletteData = data as DraggablePanelData;
      const defaultSize = TEARSHEET_PANELS[paletteData.panelType].defaultSize;
      setActiveDrag({
        source: 'palette',
        panelType: paletteData.panelType,
        w: defaultSize.w,
        h: defaultSize.h,
        offsetX: 0,
        offsetY: 0,
      });
    } else if (data?.source === 'canvas') {
      const canvasData = data as DraggableCanvasItemData;

      // Calculate offset from mouse to item's top-left corner
      let offsetX = 0;
      let offsetY = 0;

      const activatorEvent = event.activatorEvent;
      if (
        (activatorEvent instanceof PointerEvent || activatorEvent instanceof MouseEvent) &&
        canvasRef.current
      ) {
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const scale = scaleRef.current;
        // Convert mouse position to unscaled coordinates
        const mouseX = (activatorEvent.clientX - canvasRect.left) / scale;
        const mouseY = (activatorEvent.clientY - canvasRect.top) / scale;

        // Item's top-left position in unscaled pixels
        const itemLeft = canvasData.currentX * CELL_WIDTH;
        const itemTop = canvasData.currentY * CELL_HEIGHT;

        // Offset in grid cells (how far into the item the mouse clicked)
        offsetX = Math.floor((mouseX - itemLeft) / CELL_WIDTH);
        offsetY = Math.floor((mouseY - itemTop) / CELL_HEIGHT);
      }

      setActiveDrag({
        source: 'canvas',
        panelType: canvasData.panelType,
        itemId: canvasData.itemId,
        w: canvasData.w,
        h: canvasData.h,
        offsetX,
        offsetY,
      });
    }
  }, []);

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      if (!activeDrag) {
        return;
      }

      // Get pointer coordinates from the activator event
      const activatorEvent = event.activatorEvent;
      let pointerX = 0;
      let pointerY = 0;

      if (activatorEvent instanceof PointerEvent || activatorEvent instanceof MouseEvent) {
        pointerX = activatorEvent.clientX;
        pointerY = activatorEvent.clientY;
      }

      // Account for delta movement
      // Note: event.delta is guaranteed by @dnd-kit to be { x: number, y: number } during drag,
      // but we use nullish coalescing as defensive programming for edge cases during
      // drag initialization when delta might not be fully populated yet
      const currentX = pointerX + (event.delta?.x ?? 0);
      const currentY = pointerY + (event.delta?.y ?? 0);

      const gridPos = pointerToGrid(currentX, currentY);

      if (!gridPos) {
        setPreviewRect(null);
        setCursorGridPos(null);
        setIsOverCanvas(false);
        return;
      }

      // Check if pointer is within canvas bounds
      const overCanvas =
        gridPos.x >= 0 && gridPos.x < GRID_COLS && gridPos.y >= 0 && gridPos.y < GRID_MAX_ROWS;
      setIsOverCanvas(overCanvas);

      // Apply offset to get the item's top-left position
      const targetX = gridPos.x - activeDrag.offsetX;
      const targetY = gridPos.y - activeDrag.offsetY;

      // Track cursor position for invalid state display
      setCursorGridPos({ x: targetX, y: targetY });

      // Hide preview if pointer is outside the canvas bounds
      if (!overCanvas) {
        setPreviewRect(null);
        return;
      }

      // For canvas items, exclude the dragged item from occupancy grid
      const excludeItemId = activeDrag.source === 'canvas' ? activeDrag.itemId : undefined;
      const occupancyGrid = createOccupancyGrid(layout.items, excludeItemId);

      // Allow shrinking to fit available space
      const validRect = findValidDropRect(occupancyGrid, targetX, targetY, activeDrag.w, activeDrag.h, true);
      setPreviewRect(validRect);
    },
    [activeDrag, layout.items, pointerToGrid]
  );

  const handleDragEnd = useCallback(
    (_event: DragEndEvent) => {
      if (!activeDrag || !previewRect) {
        // Reset drag state
        setActiveDrag(null);
        setPreviewRect(null);
        setCursorGridPos(null);
        setIsOverCanvas(false);
        return;
      }

      if (activeDrag.source === 'palette') {
        // Add new panel from palette
        addPanel(
          activeDrag.panelType,
          { x: previewRect.x, y: previewRect.y },
          { w: previewRect.w, h: previewRect.h }
        );
      } else if (activeDrag.source === 'canvas' && activeDrag.itemId) {
        // Move existing panel (and resize if needed)
        moveAndResizePanel(activeDrag.itemId, previewRect.x, previewRect.y, previewRect.w, previewRect.h);
      }

      // Reset drag state
      setActiveDrag(null);
      setPreviewRect(null);
      setCursorGridPos(null);
      setIsOverCanvas(false);
    },
    [activeDrag, previewRect, addPanel, moveAndResizePanel]
  );

  const handleDragCancel = useCallback(() => {
    setActiveDrag(null);
    setPreviewRect(null);
    setCursorGridPos(null);
    setIsOverCanvas(false);
  }, []);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div css={containerStyle}>
        <div css={headerStyle}>
          <div css={headerLeftStyle}>
            <Typography variant='h6'>Layout Builder</Typography>
            <Typography variant='body2' color='text.secondary'>
              Drag panels from the left to build your tearsheet layout
            </Typography>
          </div>
          <Button
            variant='outlined'
            size='small'
            startIcon={<RestartAltIcon />}
            onClick={resetLayout}
            disabled={layout.items.length === 0}
          >
            Reset Layout
          </Button>
        </div>

        <div css={mainContentStyle}>
          <PanelPalette usedPanels={usedPanels} />
          <LayoutCanvas
            ref={canvasRef}
            layout={layout}
            isDragging={isOverCanvas}
            previewRect={previewRect}
            draggingItemId={activeDrag?.itemId}
            activePanelType={activeDrag?.panelType}
            cursorGridPos={cursorGridPos}
            itemSize={activeDrag ? { w: activeDrag.w, h: activeDrag.h } : undefined}
            onRemovePanel={removePanel}
            onResizePanel={resizePanel}
            onScaleChange={handleScaleChange}
          />
        </div>
      </div>

      {/* Drag overlay - follows cursor */}
      <DragOverlay dropAnimation={null}>
        {activeDrag && (
          <div css={dragOverlayStyle}>
            <Typography variant='body2' fontWeight={500}>
              {TEARSHEET_PANELS[activeDrag.panelType].label}
            </Typography>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
